import { useState, useEffect, useCallback } from 'react';
import { T, Badge, Card, Btn, ST, Inp, Sel, fmtCcy, fmtPct, fmtDate, font } from '../theme.js';

const subNav = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'debtors', label: 'Debtors' },
  { key: 'pipeline', label: 'Invoices' },
  { key: 'collections', label: 'Collections' },
  { key: 'ageing', label: 'Ageing' },
  { key: 'concentration', label: 'Concentration' },
];

const statusColor = s => ({
  ACTIVE: 'green', FUNDED: 'green', COLLECTED: 'green', APPROVED: 'blue',
  VERIFIED: 'blue', SUBMITTED: 'gray', COLLECTING: 'amber',
  OVERDUE: 'red', CHARGED_BACK: 'red', SUSPENDED: 'red',
  PENDING: 'amber', INACTIVE: 'gray',
}[s] || 'gray');

const thStyle = {
  padding: '10px 14px', textAlign: 'left', fontWeight: 600,
  color: T.textSecondary, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5,
};
const tdStyle = { padding: '12px 14px' };

const concentrationColor = pct => {
  if (pct >= 0.8) return T.red;
  if (pct >= 0.5) return T.amber;
  return T.green;
};

export default function InvoiceFinanceModule({ api, refresh }) {
  const [view, setView] = useState('dashboard');
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [facilityDetail, setFacilityDetail] = useState(null);
  const [debtors, setDebtors] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [collections, setCollections] = useState([]);
  const [ageing, setAgeing] = useState(null);
  const [concentration, setConcentration] = useState([]);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [debtorForm, setDebtorForm] = useState({ name: '', companyNumber: '', country: 'GB', creditLimit: '' });
  const [invoiceForm, setInvoiceForm] = useState({ debtorId: '', invoiceNumber: '', issueDate: '', dueDate: '', grossAmount: '', vat: '' });
  const [collectionForm, setCollectionForm] = useState({ debtorId: '', amount: '', receivedDate: '', invoiceId: '' });

  const loadFacilities = useCallback(async () => {
    try {
      const { data } = await api.get('/invoice-finance/facilities');
      setFacilities(data || []);
    } catch (e) { setError(e.message); }
  }, [api]);

  const loadFacilityDetail = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/invoice-finance/facilities/${id}/summary`);
      setFacilityDetail(data);
    } catch (e) { setError(e.message); }
  }, [api]);

  const loadDebtors = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/invoice-finance/facilities/${id}/debtors`);
      setDebtors(data || []);
    } catch (e) { setError(e.message); }
  }, [api]);

  const loadInvoices = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/invoice-finance/facilities/${id}/invoices`);
      setInvoices(data || []);
    } catch (e) { setError(e.message); }
  }, [api]);

  const loadCollections = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/invoice-finance/facilities/${id}/collections`);
      setCollections(data || []);
    } catch (e) { setError(e.message); }
  }, [api]);

  const loadAgeing = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/invoice-finance/facilities/${id}/ageing`);
      setAgeing(data);
    } catch (e) { setError(e.message); }
  }, [api]);

  const loadConcentration = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/invoice-finance/facilities/${id}/concentration`);
      setConcentration(data || []);
    } catch (e) { setError(e.message); }
  }, [api]);

  useEffect(() => { loadFacilities(); }, [loadFacilities]);

  useEffect(() => {
    if (!selectedFacility) return;
    const id = selectedFacility;
    if (view === 'dashboard') loadFacilityDetail(id);
    if (view === 'debtors') loadDebtors(id);
    if (view === 'pipeline') { loadInvoices(id); loadDebtors(id); }
    if (view === 'collections') { loadCollections(id); loadDebtors(id); loadInvoices(id); }
    if (view === 'ageing') loadAgeing(id);
    if (view === 'concentration') loadConcentration(id);
  }, [view, selectedFacility, loadFacilityDetail, loadDebtors, loadInvoices, loadCollections, loadAgeing, loadConcentration]);

  const fid = selectedFacility;

  // ── Dashboard ──
  const renderDashboard = () => (
    <div>
      <ST>Invoice Finance Facilities</ST>
      {facilities.length === 0 && <div style={{ color: T.textMuted, padding: 20 }}>No facilities found.</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        <Card style={{ padding: 14, textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: T.textMuted }}>Total Facilities</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: T.blue, marginTop: 4 }}>{facilities.length}</div>
        </Card>
        <Card style={{ padding: 14, textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: T.textMuted }}>Active</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: T.green, marginTop: 4 }}>{facilities.filter(f => f.status === 'ACTIVE').length}</div>
        </Card>
        <Card style={{ padding: 14, textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: T.textMuted }}>Total Committed</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginTop: 4 }}>{fmtCcy(facilities.reduce((s, f) => s + (f.committedAmount || 0), 0))}</div>
        </Card>
        <Card style={{ padding: 14, textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: T.textMuted }}>Total Outstanding</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.purple, marginTop: 4 }}>{fmtCcy(facilities.reduce((s, f) => s + (f.outstanding || 0), 0))}</div>
        </Card>
      </div>

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: font }}>
          <thead><tr style={{ background: T.surfaceAlt, borderBottom: `1px solid ${T.border}` }}>
            {['Customer', 'Committed', 'Outstanding', 'Availability', 'Status'].map(h =>
              <th key={h} style={thStyle}>{h}</th>
            )}
          </tr></thead>
          <tbody>{facilities.map(f => (
            <tr key={f.id} onClick={() => { setSelectedFacility(f.id); loadFacilityDetail(f.id); }}
              style={{ borderBottom: `1px solid ${T.borderLight}`, cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = T.surfaceAlt}
              onMouseLeave={e => e.currentTarget.style.background = ''}>
              <td style={{ ...tdStyle, fontWeight: 500, color: T.text }}>{f.customerName || f.customerId}</td>
              <td style={tdStyle}>{fmtCcy(f.committedAmount || 0)}</td>
              <td style={{ ...tdStyle, color: (f.outstanding || 0) > 0 ? T.purple : T.textMuted }}>{fmtCcy(f.outstanding || 0)}</td>
              <td style={tdStyle}>{fmtCcy((f.committedAmount || 0) - (f.outstanding || 0))}</td>
              <td style={tdStyle}><Badge color={statusColor(f.status)}>{f.status}</Badge></td>
            </tr>
          ))}</tbody>
        </table>
      </Card>

      {/* Selected facility detail */}
      {selectedFacility && facilityDetail && (
        <div style={{ marginTop: 24 }}>
          <ST>Facility Detail — {facilityDetail.customerName || selectedFacility}</ST>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
            <Card style={{ padding: 14 }}>
              <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>Utilisation</div>
              <div style={{ background: T.surfaceAlt, borderRadius: 6, height: 16, overflow: 'hidden', marginBottom: 6 }}>
                <div style={{
                  width: `${Math.min(100, ((facilityDetail.outstanding || 0) / (facilityDetail.committedAmount || 1)) * 100)}%`,
                  height: '100%', background: T.blue, borderRadius: 6,
                }} />
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>
                {fmtCcy(facilityDetail.outstanding || 0)} / {fmtCcy(facilityDetail.committedAmount || 0)}
                <span style={{ fontSize: 11, color: T.textSecondary, marginLeft: 8 }}>
                  ({fmtPct((facilityDetail.outstanding || 0) / (facilityDetail.committedAmount || 1))})
                </span>
              </div>
            </Card>
            <Card style={{ padding: 14 }}>
              <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>Availability</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: T.green }}>{fmtCcy((facilityDetail.committedAmount || 0) - (facilityDetail.outstanding || 0))}</div>
            </Card>
            <Card style={{ padding: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div><div style={{ fontSize: 11, color: T.textMuted }}>Active Invoices</div><div style={{ fontSize: 18, fontWeight: 700, color: T.blue }}>{facilityDetail.activeInvoices || 0}</div></div>
                <div><div style={{ fontSize: 11, color: T.textMuted }}>Overdue</div><div style={{ fontSize: 18, fontWeight: 700, color: (facilityDetail.overdueInvoices || 0) > 0 ? T.red : T.green }}>{facilityDetail.overdueInvoices || 0}</div></div>
                <div><div style={{ fontSize: 11, color: T.textMuted }}>Debtors</div><div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>{facilityDetail.debtorCount || 0}</div></div>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );

  // ── Debtors ──
  const renderDebtors = () => {
    if (!fid) return <div style={{ color: T.textMuted, padding: 20 }}>Select a facility from the Dashboard first.</div>;
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <ST>Debtor Register</ST>
          <Btn onClick={() => setShowForm(!showForm)}>{showForm ? 'Cancel' : '+ Add Debtor'}</Btn>
        </div>

        {showForm && (
          <Card style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div><label style={{ fontSize: 11, color: T.textMuted }}>Name</label><Inp value={debtorForm.name} onChange={e => setDebtorForm({ ...debtorForm, name: e.target.value })} /></div>
              <div><label style={{ fontSize: 11, color: T.textMuted }}>Company Number</label><Inp value={debtorForm.companyNumber} onChange={e => setDebtorForm({ ...debtorForm, companyNumber: e.target.value })} /></div>
              <div><label style={{ fontSize: 11, color: T.textMuted }}>Country</label><Inp value={debtorForm.country} onChange={e => setDebtorForm({ ...debtorForm, country: e.target.value })} /></div>
              <div><label style={{ fontSize: 11, color: T.textMuted }}>Credit Limit</label><Inp type="number" value={debtorForm.creditLimit} onChange={e => setDebtorForm({ ...debtorForm, creditLimit: e.target.value })} /></div>
            </div>
            <Btn onClick={async () => {
              try {
                await api.post(`/invoice-finance/facilities/${fid}/debtors`, {
                  name: debtorForm.name, companyNumber: debtorForm.companyNumber,
                  country: debtorForm.country, creditLimit: Number(debtorForm.creditLimit),
                });
                setDebtorForm({ name: '', companyNumber: '', country: 'GB', creditLimit: '' });
                setShowForm(false);
                loadDebtors(fid);
                if (refresh) refresh();
              } catch (e) { setError(e.message); }
            }}>Submit</Btn>
          </Card>
        )}

        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: font }}>
            <thead><tr style={{ background: T.surfaceAlt, borderBottom: `1px solid ${T.border}` }}>
              {['Name', 'Company No', 'Credit Limit', 'Exposure', 'Utilisation', 'Status'].map(h =>
                <th key={h} style={thStyle}>{h}</th>
              )}
            </tr></thead>
            <tbody>{debtors.map(d => (
              <tr key={d.id} style={{ borderBottom: `1px solid ${T.borderLight}` }}
                onMouseEnter={e => e.currentTarget.style.background = T.surfaceAlt}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                <td style={{ ...tdStyle, fontWeight: 500, color: T.text }}>{d.name}</td>
                <td style={{ ...tdStyle, color: T.textSecondary }}>{d.companyNumber}</td>
                <td style={tdStyle}>{fmtCcy(d.creditLimit || 0)}</td>
                <td style={tdStyle}>{fmtCcy(d.exposure || 0)}</td>
                <td style={tdStyle}>{d.creditLimit ? fmtPct((d.exposure || 0) / d.creditLimit) : '—'}</td>
                <td style={tdStyle}><Badge color={statusColor(d.status)}>{d.status || 'ACTIVE'}</Badge></td>
              </tr>
            ))}</tbody>
          </table>
        </Card>
      </div>
    );
  };

  // ── Pipeline ──
  const renderPipeline = () => {
    if (!fid) return <div style={{ color: T.textMuted, padding: 20 }}>Select a facility from the Dashboard first.</div>;

    const statuses = ['ALL', 'SUBMITTED', 'VERIFIED', 'APPROVED', 'FUNDED', 'COLLECTING', 'COLLECTED'];
    const filtered = statusFilter === 'ALL' ? invoices : invoices.filter(i => i.status === statusFilter);
    const debtorMap = Object.fromEntries(debtors.map(d => [d.id, d.name]));

    const daysOverdue = (inv) => {
      if (!inv.dueDate) return 0;
      const diff = Math.floor((Date.now() - new Date(inv.dueDate).getTime()) / 86400000);
      return diff > 0 ? diff : 0;
    };

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <ST>Invoice Pipeline</ST>
          <Btn onClick={() => setShowForm(!showForm)}>{showForm ? 'Cancel' : '+ Submit Invoice'}</Btn>
        </div>

        {/* Status filter tabs */}
        <div style={{ display: 'flex', gap: 2, marginBottom: 16 }}>
          {statuses.map(s => (
            <button key={s} onClick={() => setStatusFilter(s)} style={{
              padding: '6px 12px', fontSize: 11, fontWeight: statusFilter === s ? 600 : 400,
              color: statusFilter === s ? T.blue : T.textSecondary,
              background: statusFilter === s ? T.blueBg : 'transparent',
              border: 'none', borderRadius: 6, cursor: 'pointer', fontFamily: font,
            }}>{s}</button>
          ))}
        </div>

        {showForm && (
          <Card style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 11, color: T.textMuted }}>Debtor</label>
                <Sel value={invoiceForm.debtorId} onChange={e => setInvoiceForm({ ...invoiceForm, debtorId: e.target.value })}>
                  <option value="">Select debtor...</option>
                  {debtors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </Sel>
              </div>
              <div><label style={{ fontSize: 11, color: T.textMuted }}>Invoice Number</label><Inp value={invoiceForm.invoiceNumber} onChange={e => setInvoiceForm({ ...invoiceForm, invoiceNumber: e.target.value })} /></div>
              <div><label style={{ fontSize: 11, color: T.textMuted }}>Issue Date</label><Inp type="date" value={invoiceForm.issueDate} onChange={e => setInvoiceForm({ ...invoiceForm, issueDate: e.target.value })} /></div>
              <div><label style={{ fontSize: 11, color: T.textMuted }}>Due Date</label><Inp type="date" value={invoiceForm.dueDate} onChange={e => setInvoiceForm({ ...invoiceForm, dueDate: e.target.value })} /></div>
              <div><label style={{ fontSize: 11, color: T.textMuted }}>Gross Amount</label><Inp type="number" value={invoiceForm.grossAmount} onChange={e => setInvoiceForm({ ...invoiceForm, grossAmount: e.target.value })} /></div>
              <div><label style={{ fontSize: 11, color: T.textMuted }}>VAT</label><Inp type="number" value={invoiceForm.vat} onChange={e => setInvoiceForm({ ...invoiceForm, vat: e.target.value })} /></div>
            </div>
            <Btn onClick={async () => {
              try {
                await api.post(`/invoice-finance/facilities/${fid}/invoices`, {
                  debtorId: invoiceForm.debtorId, invoiceNumber: invoiceForm.invoiceNumber,
                  issueDate: invoiceForm.issueDate, dueDate: invoiceForm.dueDate,
                  grossAmount: Number(invoiceForm.grossAmount), vat: Number(invoiceForm.vat || 0),
                });
                setInvoiceForm({ debtorId: '', invoiceNumber: '', issueDate: '', dueDate: '', grossAmount: '', vat: '' });
                setShowForm(false);
                loadInvoices(fid);
                if (refresh) refresh();
              } catch (e) { setError(e.message); }
            }}>Submit Invoice</Btn>
          </Card>
        )}

        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: font }}>
            <thead><tr style={{ background: T.surfaceAlt, borderBottom: `1px solid ${T.border}` }}>
              {['Invoice #', 'Debtor', 'Gross', 'Advanced', 'Status', 'Due Date', 'Days Overdue', 'Actions'].map(h =>
                <th key={h} style={thStyle}>{h}</th>
              )}
            </tr></thead>
            <tbody>{filtered.map(inv => {
              const overdue = daysOverdue(inv);
              return (
                <tr key={inv.id} style={{ borderBottom: `1px solid ${T.borderLight}` }}
                  onMouseEnter={e => e.currentTarget.style.background = T.surfaceAlt}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={{ ...tdStyle, fontWeight: 500, color: T.text }}>{inv.invoiceNumber}</td>
                  <td style={{ ...tdStyle, color: T.textSecondary }}>{debtorMap[inv.debtorId] || inv.debtorId}</td>
                  <td style={tdStyle}>{fmtCcy(inv.grossAmount || 0)}</td>
                  <td style={tdStyle}>{fmtCcy(inv.advancedAmount || 0)}</td>
                  <td style={tdStyle}><Badge color={statusColor(inv.status)}>{inv.status}</Badge></td>
                  <td style={tdStyle}>{fmtDate(inv.dueDate)}</td>
                  <td style={{ ...tdStyle, color: overdue > 0 ? T.red : T.textMuted }}>{overdue > 0 ? overdue : '—'}</td>
                  <td style={tdStyle}>
                    {inv.status === 'VERIFIED' && (
                      <Btn small onClick={async () => {
                        try { await api.post(`/invoice-finance/invoices/${inv.id}/approve`); loadInvoices(fid); if (refresh) refresh(); }
                        catch (e) { setError(e.message); }
                      }}>Approve</Btn>
                    )}
                    {inv.status === 'APPROVED' && (
                      <Btn small onClick={async () => {
                        try { await api.post(`/invoice-finance/invoices/${inv.id}/fund`); loadInvoices(fid); if (refresh) refresh(); }
                        catch (e) { setError(e.message); }
                      }}>Fund</Btn>
                    )}
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
        </Card>
      </div>
    );
  };

  // ── Collections ──
  const renderCollections = () => {
    if (!fid) return <div style={{ color: T.textMuted, padding: 20 }}>Select a facility from the Dashboard first.</div>;

    const debtorMap = Object.fromEntries(debtors.map(d => [d.id, d.name]));
    const invoiceMap = Object.fromEntries(invoices.map(i => [i.id, i.invoiceNumber]));
    const unallocated = collections.filter(c => !c.invoiceId || c.status === 'UNALLOCATED');

    return (
      <div>
        <ST>Collections</ST>

        <Card style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 12 }}>Record Collection</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 11, color: T.textMuted }}>Debtor</label>
              <Sel value={collectionForm.debtorId} onChange={e => setCollectionForm({ ...collectionForm, debtorId: e.target.value })}>
                <option value="">Select debtor...</option>
                {debtors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </Sel>
            </div>
            <div><label style={{ fontSize: 11, color: T.textMuted }}>Amount</label><Inp type="number" value={collectionForm.amount} onChange={e => setCollectionForm({ ...collectionForm, amount: e.target.value })} /></div>
            <div><label style={{ fontSize: 11, color: T.textMuted }}>Received Date</label><Inp type="date" value={collectionForm.receivedDate} onChange={e => setCollectionForm({ ...collectionForm, receivedDate: e.target.value })} /></div>
            <div>
              <label style={{ fontSize: 11, color: T.textMuted }}>Invoice (optional)</label>
              <Sel value={collectionForm.invoiceId} onChange={e => setCollectionForm({ ...collectionForm, invoiceId: e.target.value })}>
                <option value="">Unallocated</option>
                {invoices.filter(i => ['FUNDED', 'COLLECTING'].includes(i.status)).map(i => (
                  <option key={i.id} value={i.id}>{i.invoiceNumber} — {fmtCcy(i.grossAmount || 0)}</option>
                ))}
              </Sel>
            </div>
          </div>
          <Btn onClick={async () => {
            try {
              await api.post('/invoice-finance/collections', {
                facilityId: fid, debtorId: collectionForm.debtorId,
                amount: Number(collectionForm.amount), receivedDate: collectionForm.receivedDate,
                invoiceId: collectionForm.invoiceId || undefined,
              });
              setCollectionForm({ debtorId: '', amount: '', receivedDate: '', invoiceId: '' });
              loadCollections(fid);
              if (refresh) refresh();
            } catch (e) { setError(e.message); }
          }}>Record Collection</Btn>
        </Card>

        {unallocated.length > 0 && (
          <>
            <ST>Unallocated Collections</ST>
            <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: font }}>
                <thead><tr style={{ background: T.surfaceAlt, borderBottom: `1px solid ${T.border}` }}>
                  {['Debtor', 'Amount', 'Received', 'Status'].map(h => <th key={h} style={thStyle}>{h}</th>)}
                </tr></thead>
                <tbody>{unallocated.map(c => (
                  <tr key={c.id} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                    <td style={{ ...tdStyle, fontWeight: 500 }}>{debtorMap[c.debtorId] || c.debtorId}</td>
                    <td style={tdStyle}>{fmtCcy(c.amount || 0)}</td>
                    <td style={tdStyle}>{fmtDate(c.receivedDate)}</td>
                    <td style={tdStyle}><Badge color="amber">{c.status || 'UNALLOCATED'}</Badge></td>
                  </tr>
                ))}</tbody>
              </table>
            </Card>
          </>
        )}

        <ST>Collection History</ST>
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: font }}>
            <thead><tr style={{ background: T.surfaceAlt, borderBottom: `1px solid ${T.border}` }}>
              {['Debtor', 'Invoice', 'Amount', 'Received', 'Status'].map(h => <th key={h} style={thStyle}>{h}</th>)}
            </tr></thead>
            <tbody>{collections.map(c => (
              <tr key={c.id} style={{ borderBottom: `1px solid ${T.borderLight}` }}
                onMouseEnter={e => e.currentTarget.style.background = T.surfaceAlt}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                <td style={{ ...tdStyle, fontWeight: 500 }}>{debtorMap[c.debtorId] || c.debtorId}</td>
                <td style={{ ...tdStyle, color: T.textSecondary }}>{invoiceMap[c.invoiceId] || c.invoiceId || '—'}</td>
                <td style={tdStyle}>{fmtCcy(c.amount || 0)}</td>
                <td style={tdStyle}>{fmtDate(c.receivedDate)}</td>
                <td style={tdStyle}><Badge color={statusColor(c.status)}>{c.status || 'ALLOCATED'}</Badge></td>
              </tr>
            ))}</tbody>
          </table>
        </Card>
      </div>
    );
  };

  // ── Ageing ──
  const renderAgeing = () => {
    if (!fid) return <div style={{ color: T.textMuted, padding: 20 }}>Select a facility from the Dashboard first.</div>;
    if (!ageing) return <div style={{ color: T.textMuted, padding: 20 }}>Loading ageing data...</div>;

    const buckets = ageing.buckets || [];
    const bucketLabels = ['Current', '1-30 days', '31-60 days', '61-90 days', '90+ days'];

    return (
      <div>
        <ST>Ageing Report</ST>
        <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: font }}>
            <thead><tr style={{ background: T.surfaceAlt, borderBottom: `1px solid ${T.border}` }}>
              {['Bucket', 'Count', 'Gross Total', 'Outstanding'].map(h => <th key={h} style={thStyle}>{h}</th>)}
            </tr></thead>
            <tbody>{buckets.map((b, i) => {
              const isOverdue90 = i === 4;
              return (
                <tr key={i} style={{ borderBottom: `1px solid ${T.borderLight}`, background: isOverdue90 ? T.redBg : '' }}>
                  <td style={{ ...tdStyle, fontWeight: 600, color: isOverdue90 ? T.red : T.text }}>{bucketLabels[i] || b.label || `Bucket ${i}`}</td>
                  <td style={tdStyle}>{b.count || 0}</td>
                  <td style={tdStyle}>{fmtCcy(b.grossTotal || 0)}</td>
                  <td style={{ ...tdStyle, color: isOverdue90 ? T.red : T.text }}>{fmtCcy(b.outstanding || 0)}</td>
                </tr>
              );
            })}</tbody>
          </table>
        </Card>

        {/* 90+ invoices with charge-back */}
        {ageing.overdueInvoices && ageing.overdueInvoices.length > 0 && (
          <>
            <ST>90+ Day Overdue Invoices</ST>
            <Card style={{ padding: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: font }}>
                <thead><tr style={{ background: T.surfaceAlt, borderBottom: `1px solid ${T.border}` }}>
                  {['Invoice #', 'Debtor', 'Gross', 'Outstanding', 'Days Overdue', 'Action'].map(h => <th key={h} style={thStyle}>{h}</th>)}
                </tr></thead>
                <tbody>{ageing.overdueInvoices.map(inv => (
                  <tr key={inv.id} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                    <td style={{ ...tdStyle, fontWeight: 500, color: T.red }}>{inv.invoiceNumber}</td>
                    <td style={{ ...tdStyle, color: T.textSecondary }}>{inv.debtorName || inv.debtorId}</td>
                    <td style={tdStyle}>{fmtCcy(inv.grossAmount || 0)}</td>
                    <td style={{ ...tdStyle, color: T.red }}>{fmtCcy(inv.outstanding || 0)}</td>
                    <td style={{ ...tdStyle, color: T.red }}>{inv.daysOverdue}</td>
                    <td style={tdStyle}>
                      <Btn small onClick={async () => {
                        try {
                          await api.post(`/invoice-finance/invoices/${inv.id}/charge-back`);
                          loadAgeing(fid);
                          if (refresh) refresh();
                        } catch (e) { setError(e.message); }
                      }}>Charge Back</Btn>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </Card>
          </>
        )}
      </div>
    );
  };

  // ── Concentration ──
  const renderConcentration = () => {
    if (!fid) return <div style={{ color: T.textMuted, padding: 20 }}>Select a facility from the Dashboard first.</div>;

    return (
      <div>
        <ST>Concentration Report</ST>
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: font }}>
            <thead><tr style={{ background: T.surfaceAlt, borderBottom: `1px solid ${T.border}` }}>
              {['Debtor', 'Exposure', 'Credit Limit', 'Limit Utilisation', 'Facility %', 'Status'].map(h =>
                <th key={h} style={thStyle}>{h}</th>
              )}
            </tr></thead>
            <tbody>{concentration.map(c => {
              const limitPct = c.creditLimit ? (c.exposure || 0) / c.creditLimit : 0;
              return (
                <tr key={c.debtorId || c.id} style={{ borderBottom: `1px solid ${T.borderLight}` }}
                  onMouseEnter={e => e.currentTarget.style.background = T.surfaceAlt}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={{ ...tdStyle, fontWeight: 500, color: T.text }}>{c.debtorName || c.name}</td>
                  <td style={tdStyle}>{fmtCcy(c.exposure || 0)}</td>
                  <td style={tdStyle}>{fmtCcy(c.creditLimit || 0)}</td>
                  <td style={{ ...tdStyle, color: concentrationColor(limitPct), fontWeight: 600 }}>{fmtPct(limitPct)}</td>
                  <td style={tdStyle}>{fmtPct(c.facilityPct || 0)}</td>
                  <td style={tdStyle}><Badge color={limitPct >= 0.8 ? 'red' : limitPct >= 0.5 ? 'amber' : 'green'}>{c.status || (limitPct >= 0.8 ? 'HIGH' : limitPct >= 0.5 ? 'MEDIUM' : 'LOW')}</Badge></td>
                </tr>
              );
            })}</tbody>
          </table>
        </Card>
      </div>
    );
  };

  // ── Main render ──
  return (
    <div style={{ fontFamily: font }}>
      {error && (
        <div style={{ background: T.redBg, border: `1px solid ${T.redBorder}`, color: T.red, padding: '8px 14px', borderRadius: 6, marginBottom: 12, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{error}</span>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: T.red, cursor: 'pointer', fontWeight: 600 }}>x</button>
        </div>
      )}

      {/* Sub-nav */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: `1px solid ${T.border}`, overflowX: 'auto' }}>
        {subNav.map(n => {
          const isActive = view === n.key;
          return (
            <button key={n.key} onClick={() => { setView(n.key); setShowForm(false); setStatusFilter('ALL'); }} style={{
              padding: '8px 14px', fontSize: 12, fontWeight: isActive ? 600 : 400,
              color: isActive ? T.blue : T.textSecondary,
              background: isActive ? T.blueBg : 'transparent',
              border: 'none', borderBottom: isActive ? `2px solid ${T.blue}` : '2px solid transparent',
              cursor: 'pointer', fontFamily: font, whiteSpace: 'nowrap',
            }}>{n.label}</button>
          );
        })}
      </div>

      {view === 'dashboard' && renderDashboard()}
      {view === 'debtors' && renderDebtors()}
      {view === 'pipeline' && renderPipeline()}
      {view === 'collections' && renderCollections()}
      {view === 'ageing' && renderAgeing()}
      {view === 'concentration' && renderConcentration()}
    </div>
  );
}
